import { Validator } from '../public'
import { validateShape } from '../shape/validateShape'
import { ValidatorOrShape } from './types'

/**
 * Creates a validator that allows null or undefined values.
 * When a value is provided, it is validated using the given validator or shape.
 * 
 * @param validatorOrShape - The validator or shape to use for non-null values
 * @returns A validator that allows null/undefined values
 */
export function optionalize(
  validatorOrShape: ValidatorOrShape
): Validator {
  return (value: unknown, field?: string) => {
    if (value === null || value === undefined) return [{ valid: true, field: field ?? '', value }]

    if (typeof validatorOrShape === 'function') {
      return (validatorOrShape as Validator)(value, field)
    }

    return validateShape(value, validatorOrShape, { soft: true }).results
  }
}

/**
 * Creates a validator for arrays of values.
 * Each element in the array is validated using the given validator or shape.
 * 
 * @param validatorOrShape - The validator or shape to use for array elements
 * @returns A validator for arrays
 */
export function arrayOf(
  validatorOrShape: ValidatorOrShape
): Validator {
  return (value: unknown, parentField?: string) => {
    if (!Array.isArray(value)) {
      return [{
        valid: false,
        field: parentField ?? '',
        value,
        message: 'Expected an array'
      }]
    }

    const allResults = value.flatMap((item, index) => {
      const fieldPrefix = parentField ? `${parentField}[${index}]` : `[${index}]`

      if (typeof validatorOrShape === 'function') {
        return validatorOrShape(item, fieldPrefix)
      }

      const { results } = validateShape(item, validatorOrShape, { soft: true })
      return results.map(res => ({
        ...res,
        field: res.field ? `${fieldPrefix}.${res.field}` : fieldPrefix
      }))
    })

    return allResults
  }
}

/**
 * Creates a validator for arrays that allows null or undefined values.
 * When an array is provided, each element is validated using the given validator or shape.
 * 
 * @param validatorOrShape - The validator or shape to use for array elements
 * @returns A validator for optional arrays
 */
export function optionalArrayOf(
  validatorOrShape: ValidatorOrShape
): Validator {
  return optionalize(arrayOf(validatorOrShape))
}